import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { candidatesKeys } from "@/application/query-keys/candidates.keys";
import {
  candidatesRepository,
  type UpsertCandidateInput,
} from "@/infrastructure/repositories/candidates.supabase.repository";

export function useCandidates() {
  return useQuery({
    queryKey: candidatesKeys.list(),
    queryFn: () => candidatesRepository.list(),
  });
}

export function useCandidate(slug: string | undefined) {
  return useQuery({
    queryKey: candidatesKeys.detail(slug ?? ""),
    queryFn: () => (slug ? candidatesRepository.getBySlug(slug) : Promise.resolve(null)),
    enabled: typeof slug === "string" && slug.length > 0,
  });
}

export function useUpsertCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertCandidateInput) => candidatesRepository.upsert(input),
    onSettled: (_data, _err, input) => {
      void qc.invalidateQueries({ queryKey: candidatesKeys.all });
      void qc.invalidateQueries({ queryKey: candidatesKeys.detail(input.slug) });
    },
  });
}

export function useRemoveCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => candidatesRepository.remove(id),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: candidatesKeys.all });
    },
  });
}

export function useDuplicateCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      newSlug,
      newName,
    }: {
      sourceId: string;
      newSlug: string;
      newName: string;
    }) => candidatesRepository.duplicate(sourceId, newSlug, newName),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: candidatesKeys.all });
    },
  });
}
